import React, { useState, useEffect, useRef } from 'react'
import { useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import { useInterval } from '@renderer/utils/useInterval'
import cogoToast from '@successtar/cogo-toast'
import Preview from '@renderer/components/Preview'
import Spinner from '@renderer/components/Spinner'
import { flashEffect } from '@renderer/utils/flashUtils'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import { filterUtils } from '@renderer/utils/filterUtils'
import { applyLUTThenOverlay, applyFilterOverlayImage} from '@renderer/utils/lutUtils'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { usePrintStore } from '@renderer/stores/printStore'
import { initiatePrint } from '@renderer/utils/pipeline/printService'
import MidtransUtils from '@renderer/utils/midtransUtils'
import PrintPreview, { PrintPreviewRef } from '@renderer/components/PrintPreview'
import { FrameOption } from '@renderer/types/configState'
import { FaMoneyBillWave } from 'react-icons/fa'
import { useCameraStore } from '@renderer/stores/cameraStore'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'
import { capabilities } from '@renderer/lib/runtime/capabilities'
import { printThermalEscpos, shouldUseThermalPrint } from '@renderer/lib/runtime/thermalPrint'
import { blobToBase64DataUrl, exportThermalImageBlob } from '@renderer/utils/exportThermalImage'
import BahelaThermalSettings from './BahelaThermalSettings'
import PinModal from '@renderer/components/PinModal'
import OnScreenKeyboard from '@renderer/components/OnScreenKeyboard'
import { useFeatureFlag } from '@renderer/hooks/useFeatureFlag'
import { useDeviceOrientation } from '@renderer/hooks'
import { getDesignCanvasSizeForOrientation } from '@renderer/utils/designCanvasSize'
import {
  frameWidthCss,
  windowStyle,
  artAspectCss,
  KARCIS_STAGE_POSITIONS,
  type KarcisArtKey
} from './karcisLayout'
import {
  finishKarcisSession,
  recordKarcisPayment,
  startKarcisSession
} from './karcisSessionLifecycle'
import type { KarcisPaymentParams } from './karcisSession'
import {
  KARCIS_TV,
  karcisStageCeilingMs,
  ticketsPerSession,
  type KarcisVariant
} from './karcisVariant'
import {
  payableKarcisAmount,
  readKarcisVoucher,
  type KarcisVoucherApplication
} from './karcisVoucher'

// Import new Bahela assets
import background from '/assets/bahela-karcis/background.webp'
import karcisKenanganBig from '/assets/bahela-karcis/karcis-kenangan-big.webp'
import mulaiSesi from '/assets/bahela-karcis/mulai-sesi.webp'
import bahela from '/assets/bahela-karcis/bahela.webp'
import karcisKenanganSmall from '/assets/bahela-karcis/karcis-kenangan-small.webp'
import pratinjau from '/assets/bahela-karcis/pratinjau.webp'
import bayar from '/assets/bahela-karcis/bayar.webp'
import pemutarFoto from '/assets/bahela-karcis/pemutar-foto.webp'
import diproses from '/assets/bahela-karcis/diproses.webp'
import pemutarMedia from '/assets/bahela-karcis/pemutar-media.webp'
import silahkanDisobek from '/assets/bahela-karcis/silahkan-disobek.webp'

type Stage = 1 | 2 | 3 | 4 | 5

/**
 * A retro-window art frame plus its measured content window (see karcisLayout).
 * The frame box is sized by BOTH canvas-width and canvas-height budgets, so it
 * can never outgrow its stage on any canvas aspect; children render clipped
 * inside the art's real interior region.
 */
const ArtFrame: React.FC<{
  art: string
  artKey: KarcisArtKey
  alt: string
  hOverW: number
  className?: string
  shadowClass?: string
  children?: React.ReactNode
}> = ({ art, artKey, alt, hOverW, className = '', shadowClass = 'drop-shadow-xl', children }) => (
  <div className={`relative ${className}`} style={{ width: frameWidthCss(artKey, hOverW) }}>
    <img
      src={art}
      alt={alt}
      className={`block w-full h-auto ${shadowClass}`}
      style={{ aspectRatio: artAspectCss(artKey) }}
    />
    {children !== undefined && (
      <div className="absolute overflow-hidden" style={windowStyle(artKey)}>
        {children}
      </div>
    )}
  </div>
)

interface Transaction {
  orderId: string | null
  qrUrl?: string
  expiryTime?: string
  serverTime?: string
  amount: number
  status: string
}

/** Price of one karcis, in the project's native currency. */
const KARCIS_PRICE = 5000

/**
 * The voucher half of a payment record, or nothing at all.
 *
 * Spread into every `recordKarcisPayment` call on this page so a session
 * carries its voucher from the first quote through to settlement. `voucherId`
 * on a SETTLED session is what makes `POST /api/sessions` decrement the code's
 * `limitUsage` — the only thing that burns a quota when no gateway webhook ever
 * fires, which is exactly the free-session case.
 */
function voucherFields(
  voucher: KarcisVoucherApplication | null
): Partial<KarcisPaymentParams> {
  if (!voucher) return {}
  return {
    discount: voucher.discount,
    voucherCode: voucher.code,
    voucherId: voucher.voucherId,
    voucherType: voucher.voucherType,
    voucherValue: voucher.voucherValue
  }
}
/** Delay before asking for a fresh QRIS quote after a failed/unusable one. */
const QR_RETRY_MS = 5000
/** After this long an in-flight quote request counts as hung and may be superseded. */
const QR_REQUEST_TIMEOUT_MS = 20000
/**
 * Last-resort re-arm: how long the payment stage may sit on the "Loading QR Code…"
 * spinner with nothing in flight before we ask for a quote again. The booth is
 * unattended — a stalled QR is a dead booth until someone notices.
 */
const QR_WATCHDOG_MS = 15000
/**
 * How often the supervisor re-derives the truth from the wall clock. This is the
 * one timer on the page that is not gated behind any state, so it is also the one
 * that recovers the page when every other timer has been suspended.
 */
const SUPERVISOR_MS = 2000
/**
 * How long the captured photo stays on screen before the next countdown starts,
 * when a session prints more than one karcis. Long enough to read as "that one
 * is in the bag", short enough that three shots don't feel like three sessions.
 */
const SHOT_REVIEW_MS = 1200

/** Grace period for the hidden PrintPreview canvases to mount and load. */
const COMPOSE_SETTLE_MS = 3000

interface BahelaReceiptPageProps {
  /**
   * Which karcis this is. Defaults to the analog-TV booth, so every existing
   * `bahelastruk` project keeps the behaviour it has today; the touch profile
   * arrives only through the `bahelastruk-touch` route. See karcisVariant.ts.
   */
  variant?: KarcisVariant
}

const BahelaReceiptPage: React.FC<BahelaReceiptPageProps> = ({ variant = KARCIS_TV }) => {
  const { projectId } = useParams()
  const { config, getFilter, filters, frames, getFrame } = useConfigStore()
  const { id, userPreferences } = useSessionStateStore()
  const { cameraSettings, selectedCamera, password } = useSettingsStore()
  const { getPrinterForFrame } = usePrintStore()
  const { camera, webcam } = useCameraStore()
  const isMounted = useRef(true)

  // States
  const [currentStage, setCurrentStage] = useState<Stage>(1)
  const [remainingTime, setRemainingTime] = useState<string | null>(null)
  const [qrUrl, setQrUrl] = useState<string | null>(null)
  const [isCheckingPayment, setIsCheckingPayment] = useState(false)
  const [capturedImageURL, setCapturedImageURL] = useState<string | null>(null)
  /**
   * Every shot of this session, in order — one printed karcis each.
   *
   * A one-photo session leaves this at length 1 and behaves exactly as before;
   * `capturedImageURL` remains "the shot currently on screen" so stage 3 can
   * flash the photo back at the guest between countdowns.
   */
  const [capturedPhotos, setCapturedPhotos] = useState<string[]>([])
  /** Stage 5 cycles through a multi-shot session so every karcis gets seen. */
  const [resultPhotoIndex, setResultPhotoIndex] = useState(0)
  const [captureCountdown, setCaptureCountdown] = useState<number>(10)
  const [transaction, setTransaction] = useState<Transaction>({
    orderId: null,
    amount: KARCIS_PRICE,
    status: 'pending'
  })
  const [selectedFrame, setSelectedFrame] = useState<FrameOption | undefined>(undefined)
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>([])
  const [printProgress, setPrintProgress] = useState(0)
  const [isBlinking, setIsBlinking] = useState(false)
  const [thermalSettingsOpen, setThermalSettingsOpen] = useState(false)
  /**
   * Which operator action is waiting on the booth PIN, if any.
   *
   * Two corners of this screen do things no guest should be able to do. On the
   * TV booth neither is reachable; on an iPad both are one tap away, so the
   * touch variant routes both through here.
   */
  const [pinPrompt, setPinPrompt] = useState<'bypass' | 'printer' | null>(null)
  /**
   * One PrintPreview per karcis, so each composition is bound to a fixed photo
   * for its whole life.
   *
   * The alternative — one preview re-pointed at each photo in turn — reads
   * simpler and is exactly the stale-render trap this page has fallen into
   * before (#199, and the frame/photo guard in `startPrintingImmediately`):
   * `exportImage` waits for "assets loaded", which a not-yet-re-rendered
   * preview reports true for while still holding the PREVIOUS photo. Three
   * identical karcis, no error anywhere. Separate refs make that unrepresentable.
   */
  const printPreviewRefs = useRef<(PrintPreviewRef | null)[]>([])
  const printProgressIntervalRef = useRef<NodeJS.Timeout | null>(null)
  // Blob URLs of this session's captures, so they can be revoked instead of leaking per session
  const capturedPhotoUrlsRef = useRef<string[]>([])
  /**
   * Shots banked so far this cycle. A ref, not state, because the capture
   * countdown freezes its callback (see the `latest` bag below) and would
   * otherwise re-read "0 shots taken" on every shot and loop forever.
   */
  const shotsTakenRef = useRef(0)
  // Quote bookkeeping: `startedAt` is null when nothing is in flight, and `seq`
  // identifies the newest request so a slow one can't overwrite a newer QR.
  const qrRequestStartedAtRef = useRef<number | null>(null)
  const qrRequestSeqRef = useRef(0)
  const qrRetryTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const expiredReloadTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  /**
   * Wall-clock deadline of the QR currently on screen, and the order it belongs to.
   *
   * The countdown that used to own expiry decrements a counter once per tick, so
   * it measures ticks, not time: throttle or suspend the renderer and 120 "seconds"
   * can span half an hour, leaving a long-dead QR on screen with minutes still
   * showing. Everything that decides whether the QR is still good now reads this
   * instead, so a resumed page notices immediately however long it was away.
   */
  const qrExpiresAtRef = useRef<number | null>(null)
  const qrOrderIdRef = useRef<string | null>(null)
  /** When the page entered the stage it is on — drives the stuck-stage ceilings. */
  const stageEnteredAtRef = useRef<number>(Date.now())
  /**
   * The karcis session currently being recorded, or null between cycles.
   *
   * This page is one component that loops forever, so it has no unmount/mount
   * boundary to hang a session off — the ref is the boundary. Everything that
   * uploads (session state, activity logs, the gateway quote) reads the id from
   * here rather than from the store, so a cycle that ends mid-flight can never
   * write into the next customer's session.
   */
  const sessionIdRef = useRef<string | null>(null)

  // Countdown hooks
  const paymentCountdown = useCountdown(120)
  const captureCountdownHook = useCountdown(10, { audioCue: true })
  const autoStartCountdown = useCountdown(5)
  const resultDisplayCountdown = useCountdown(10)
  const waitCountdown = useCountdown(5)

  const selectedFilter = getFilter(userPreferences.filterId || filters[0]._id)

  /**
   * Karcis printed per paid session, each with its own photo.
   *
   * `captureCount` normally says how many photos fill a frame's slots; a karcis
   * frame has exactly one slot, so on this flow the same number means how many
   * tickets come off the roll — Dhafin's "sekali foto 3 jadi kyk berderet".
   * The TV variant pins it to 1 regardless of what the project carries; see
   * karcisVariant.ts for why that guard is not optional.
   */
  const ticketCount = ticketsPerSession(variant, config?.pages?.capture?.settings?.captureCount)

  /**
   * The voucher applied to the session on screen, if any. Cleared by
   * `resetToStage1` along with everything else the outgoing customer left
   * behind — a code that survived into the next cycle would give the next
   * guest a free session and burn this one's quota twice.
   */
  const [voucher, setVoucher] = useState<KarcisVoucherApplication | null>(null)
  const [voucherOpen, setVoucherOpen] = useState(false)
  const [voucherError, setVoucherError] = useState('')
  const [voucherApplying, setVoucherApplying] = useState(false)

  /**
   * What the guest still owes. Without a voucher this is KARCIS_PRICE and every
   * figure on the payment path is the one it has always been — which is what
   * keeps the analog-TV karcis, where no code can be entered at all, unchanged.
   */
  const payableAmount = payableKarcisAmount(KARCIS_PRICE, voucher)

  /**
   * Three gates, all of which have to be open: the hardware can take typed
   * input (karcisVariant), the project wants vouchers (the same
   * voucherEnabled/promoEnabled pair every other brand's checkout reads), and
   * the feature is switched on for this account in the Studio.
   */
  const rewardFlagOn = useFeatureFlag('karcis_reward_voucher')
  const projectAllowsVouchers =
    config?.pages?.checkout?.settings?.voucherEnabled ??
    config?.pages?.checkout?.settings?.promoEnabled ??
    false
  const canEnterVoucher = variant.allowVoucherEntry && projectAllowsVouchers && rewardFlagOn

  // Check if camera is connected and available
  const isCameraConnected = Boolean(selectedCamera && (camera || webcam))

  // Stage panel `top` AND the strip's scroll target — one shared table
  // (KARCIS_STAGE_POSITIONS), so a panel can never drift from its visible
  // window again. See karcisLayout.ts for the history.
  const stageTop = (stage: Stage): string => `${KARCIS_STAGE_POSITIONS[stage]}%`

  /**
   * Activity log scoped to the live cycle. Everything this booth knows about a
   * failure — a camera that returned nothing, a printer that refused the job —
   * only becomes visible from the backend through here.
   */
  const logKarcis = (
    message: string,
    details: Record<string, unknown>,
    page: string
  ): void => {
    useSessionStateStore
      .getState()
      .logActivity(message, details, page, sessionIdRef.current ?? undefined)
  }

  const calculateRemainingTime = (expiryTime: string, serverTime: string): number => {
    try {
      const expiryTimestamp = new Date(expiryTime).getTime()
      const serverTimestamp = new Date(serverTime).getTime()
      const remainingMs = expiryTimestamp - serverTimestamp
      return Math.max(0, Math.floor(remainingMs / 1000))
    } catch (error) {
      console.error('Error calculating remaining time:', error)
      return 0
    }
  }

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
  }

  /** Seconds left on the QR by the wall clock; 0 once it has lapsed. */
  const remainingSecondsFromClock = (): number => {
    const expiresAt = qrExpiresAtRef.current
    if (expiresAt === null) return 0
    return Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000))
  }

  /** True when a QR is on screen whose wall-clock deadline has already passed. */
  const qrHasLapsed = (): boolean =>
    qrExpiresAtRef.current !== null && Date.now() >= qrExpiresAtRef.current

  const clearQrRetry = (): void => {
    if (qrRetryTimeoutRef.current) {
      clearTimeout(qrRetryTimeoutRef.current)
      qrRetryTimeoutRef.current = null
    }
  }

  /**
   * Re-ask for a quote after a failure. Without this a single hiccup — a Wi-Fi
   * blip right after boot, or a quote that lands already expired — leaves the
   * payment stage on its spinner forever, with no error the operator can see.
   */
  const scheduleQrRetry = (reason: string): void => {
    if (!isMounted.current) return
    console.warn(`[BahelaReceipt] QR unavailable (${reason}) — retrying in ${QR_RETRY_MS}ms`)
    clearQrRetry()
    qrRetryTimeoutRef.current = setTimeout(() => {
      qrRetryTimeoutRef.current = null
      if (!isMounted.current || latest.current.currentStage !== 2) return
      void latest.current.initiatePayment()
    }, QR_RETRY_MS)
  }

  const initiatePayment = async (): Promise<void> => {
    // One quote at a time — expiry, retry and watchdog can all land together. A
    // request that has outlived QR_REQUEST_TIMEOUT_MS is treated as hung and
    // superseded; `fetch` here has no timeout of its own, and a booth that boots
    // before its Wi-Fi does can otherwise wait on it forever.
    const inFlightSince = qrRequestStartedAtRef.current
    if (inFlightSince !== null && Date.now() - inFlightSince < QR_REQUEST_TIMEOUT_MS) return

    const requestId = ++qrRequestSeqRef.current
    qrRequestStartedAtRef.current = Date.now()
    clearQrRetry()

    try {
      // Quote what is actually owed. `latest` rather than the captured value:
      // timers and the watchdog re-enter here through frozen closures, and a
      // voucher applied since one was made would otherwise be quoted around.
      const owed = latest.current.payableAmount
      const appliedVoucher = latest.current.voucher
      const data = await MidtransUtils.initiatePayment(
        owed,
        sessionIdRef.current || '',
        120,
        projectId || '',
        userPreferences?.filterId || '',
        userPreferences?.frameId || '',
        appliedVoucher?.discount ?? 0
      )

      // A newer request took over while this one was out; its QR is the live one.
      if (requestId !== qrRequestSeqRef.current || !isMounted.current) return

      if (!data.order_id || !data.qr_url) {
        scheduleQrRetry('response carried no QR')
        return
      }

      const sanitizedQrUrl = data.qr_url.replace(/([^:]\/)\/+/g, '$1')
      const initialRemainingSeconds = calculateRemainingTime(data.expiry_time, data.server_time)

      if (initialRemainingSeconds <= 0) {
        scheduleQrRetry('quote arrived already expired')
        return
      }

      setTransaction({
        orderId: data.order_id,
        qrUrl: sanitizedQrUrl,
        expiryTime: data.expiry_time,
        serverTime: data.server_time,
        status: 'pending',
        amount: owed
      })

      setQrUrl(sanitizedQrUrl)

      recordKarcisPayment(sessionIdRef.current || '', {
        orderId: data.order_id,
        amount: owed,
        status: 'pending',
        qrUrl: sanitizedQrUrl,
        expiryTime: data.expiry_time,
        serverTime: data.server_time,
        ...voucherFields(appliedVoucher)
      })

      // Anchor expiry to the wall clock before arming anything. The countdown
      // below is now only a display; this ref is what actually decides whether
      // the QR on screen is still valid.
      qrOrderIdRef.current = data.order_id
      qrExpiresAtRef.current = Date.now() + initialRemainingSeconds * 1000
      setRemainingTime(formatTime(initialRemainingSeconds))

      paymentCountdown.setRemainingSeconds(initialRemainingSeconds)
      paymentCountdown.setOnTick(() => {
        setRemainingTime(formatTime(remainingSecondsFromClock()))
      })
      paymentCountdown.setOnComplete(() => latest.current.handlePaymentExpired(data.order_id))
      paymentCountdown.start()
      setIsCheckingPayment(true)
    } catch (e: unknown) {
      console.error('Error initiating payment:', e)
      if (requestId !== qrRequestSeqRef.current) return
      cogoToast.error((e as Error).message)
      scheduleQrRetry('request failed')
    } finally {
      if (requestId === qrRequestSeqRef.current) {
        qrRequestStartedAtRef.current = null
      }
    }
  }

  /**
   * Retire the QR identified by `orderId` and line up a fresh one.
   *
   * Scoped to an order because two things can report the same expiry — the
   * display countdown and an in-flight status poll — and a poll answering for the
   * *previous* order used to arrive after the replacement QR was already on
   * screen, cancelling the live order server-side and starting the cycle again.
   * Passing no id means "whatever is current" (the supervisor's own sweep).
   */
  const handlePaymentExpired = (orderId?: string): void => {
    const liveOrderId = qrOrderIdRef.current
    if (orderId && liveOrderId && orderId !== liveOrderId) {
      console.warn(
        `[BahelaReceipt] ignoring expiry for superseded order ${orderId} (live: ${liveOrderId})`
      )
      return
    }

    // Mark the QR dead immediately. Until this ref is cleared the supervisor sees
    // a valid QR and stays out of the way, so it has to happen before the awaits.
    qrExpiresAtRef.current = null
    qrOrderIdRef.current = null

    setIsCheckingPayment(false)
    setRemainingTime('00:00')

    // Only when an order actually lapsed — the supervisor also routes through
    // here on its own sweep, and logging an expiry for a QR that never existed
    // is noise in the one place an operator goes looking for signal.
    if (liveOrderId) {
      recordKarcisPayment(sessionIdRef.current || '', {
        orderId: liveOrderId,
        amount: latest.current.payableAmount,
        status: 'expire',
        qrUrl: null
      })
    }

    // Cancel the expired order, but never block the reload on it: this DELETE has
    // no timeout, and awaiting a hung one used to leave a dead 00:00 QR on screen.
    // Pin the id so a reload that lands first can't redirect this to the new order.
    void cleanupPayment(liveOrderId || undefined)

    if (expiredReloadTimeoutRef.current) clearTimeout(expiredReloadTimeoutRef.current)
    expiredReloadTimeoutRef.current = setTimeout(() => {
      expiredReloadTimeoutRef.current = null
      if (!isMounted.current) return

      setQrUrl(null)
      // Reset transaction state before new payment. The voucher is NOT cleared:
      // a QR lapsing is the booth's problem, not the guest's, and making them
      // re-type a code because they read the screen slowly is the support call
      // this feature exists to prevent.
      setTransaction({
        orderId: null,
        amount: latest.current.payableAmount,
        status: 'pending'
      })
      // Only reload QR if still on stage 2 (payment stage). Read the stage through
      // `latest` — this callback was frozen by the countdown at the render that
      // started it, where `currentStage` was still 1 (boot) or the print stage.
      // If the stage moved on, the supervisor picks it up again on return.
      if (latest.current.currentStage === 2) {
        void latest.current.initiatePayment()
      }
    }, 3000)
  }

  const checkPaymentStatus = async (): Promise<void> => {
    if (!transaction?.expiryTime || !transaction?.serverTime) return

    const polledOrderId = transaction.orderId
    if (!polledOrderId) return

    try {
      const paymentData = await MidtransUtils.checkPaymentStatus(polledOrderId)

      // Paid wins unconditionally, even for an order we have already retired: the
      // customer's money is taken, so the session has to start regardless of what
      // the expiry bookkeeping thinks.
      if (paymentData.paid) {
        qrExpiresAtRef.current = null
        qrOrderIdRef.current = null
        setIsCheckingPayment(false)
        setQrUrl(null) // Clear QR URL when payment succeeds
        setTransaction({ ...transaction, status: 'settlement' })
        // The customer's money is taken — push it to the server now rather than
        // waiting for the end of the cycle, which a power cut or a stuck printer
        // may never reach.
        recordKarcisPayment(
          sessionIdRef.current || '',
          {
            orderId: polledOrderId,
            amount: payableAmount,
            status: 'settlement',
            qrUrl: null,
            expiryTime: transaction.expiryTime,
            serverTime: transaction.serverTime,
            engagedAt: new Date(),
            ...voucherFields(voucher)
          },
          { upload: true }
        )
        cogoToast.success('Pembayaran berhasil! Siap-siap foto!')
        // Only advance to stage 3 if we're currently on stage 2 (payment stage)
        if (currentStage === 2) {
          setCurrentStage(3)
          startCaptureCountdown()
        }
        return
      }

      // Expiry, on the other hand, only counts for the QR actually on screen. A
      // reload may have replaced it while this request was out, and acting on the
      // stale answer used to cancel the fresh order seconds after it appeared —
      // which looks, from the front of the booth, exactly like the QR never loading.
      if (qrOrderIdRef.current !== polledOrderId) return

      if (paymentData.isExpired && isMounted.current) {
        handlePaymentExpired(polledOrderId)
      }
    } catch (e: unknown) {
      console.error('Error checking payment status:', e)
    }
  }

  const startCaptureCountdown = (): void => {
    captureCountdownHook.setOnTick(() => {
      setCaptureCountdown(captureCountdownHook.getRemainingSeconds())
    })

    captureCountdownHook.setOnComplete(() => {
      void latest.current.handleCapture()
    })

    // Autofocus now, while the countdown runs, so the shutter is not waiting on it (camera_prefocus).
    unifiedCameraUtils.prefocus()
    captureCountdownHook.start()
  }

  const handleCapture = async (): Promise<void> => {
    try {
      flashEffect('.flash')

      await new Promise((resolve) => setTimeout(resolve, 100))

      const imageData = await unifiedCameraUtils.captureImage(
        userPreferences.selectedCamera as 'primary' | 'secondary',
        applyFilter,
        undefined,
        !cameraSettings.useShutterCapture
      )

      if (imageData) {
        const imageUrl = URL.createObjectURL(imageData)
        capturedPhotoUrlsRef.current = [...capturedPhotoUrlsRef.current, imageUrl]
        shotsTakenRef.current += 1
        const shotsTaken = shotsTakenRef.current
        const tickets = latest.current.ticketCount
        logKarcis('Karcis photo captured', { shot: shotsTaken, of: tickets }, 'capture')

        setCapturedImageURL(imageUrl)
        setCapturedPhotos([...capturedPhotoUrlsRef.current])

        // More karcis to shoot: show the guest what they just got, then run the
        // countdown again. The photos accumulate in the ref rather than in state
        // because this callback is frozen (see `latest`) and would otherwise
        // rebuild the list from whatever `capturedPhotos` was at arming time.
        if (shotsTaken < tickets) {
          setTimeout(() => {
            if (!isMounted.current) return
            setCapturedImageURL(null)
            startCaptureCountdown()
          }, SHOT_REVIEW_MS)
          return
        }

        const photos = [...capturedPhotoUrlsRef.current]
        const frame = getFrame(userPreferences.frameId || (frames[0]?._id ?? ''))
        setSelectedFrame(frame)
        setSelectedPhotos(photos)

        // Give the hidden PrintPreview canvases a beat to mount and load before
        // composing. Pass frame/photos explicitly: the countdown freezes this
        // callback in a ref, so reading selectedFrame/selectedPhotos from state
        // here would see the pre-capture values — undefined/[] on the first
        // session after boot or refresh.
        setTimeout(() => {
          setCurrentStage(4)
          startPrintingImmediately(frame, photos)
        }, COMPOSE_SETTLE_MS)
      } else {
        logKarcis('Karcis capture returned no image — retrying', {}, 'capture')
        cogoToast.error('Gagal mengambil foto, coba lagi!')
        startCaptureCountdown()
      }
    } catch (error) {
      console.error('Capture failed:', error)
      logKarcis(
        'Karcis capture failed — retrying',
        { error: error instanceof Error ? error.message : String(error) },
        'capture'
      )
      cogoToast.error('Gagal mengambil foto, coba lagi!')
      startCaptureCountdown()
    }
  }

  const startPrintingImmediately = async (
    frame: FrameOption | undefined,
    photos: string[]
  ): Promise<void> => {
    const previews = printPreviewRefs.current.slice(0, photos.length)
    const composersReady = previews.length === photos.length && previews.every(Boolean)

    if (!composersReady || !frame || photos.length === 0) {
      console.error('[BahelaReceipt] Print skipped — nothing to compose', {
        composers: previews.filter(Boolean).length,
        hasFrame: Boolean(frame),
        photoCount: photos.length
      })
      logKarcis(
        'Karcis print skipped — nothing to compose',
        {
          composers: previews.filter(Boolean).length,
          hasFrame: Boolean(frame),
          photoCount: photos.length
        },
        'print'
      )
      cogoToast.error('Gagal mencetak karcis: foto belum siap')
      startPrintingProgress()
      endKarcisCycle(false, 'nothing_to_compose')
      return
    }

    /** Stamps this print cycle, so ticket keys can never repeat across sessions. */
    const cycleKey = `karcis-${Date.now()}`

    /**
     * One karcis. Composed from its own preview and printed on its own job —
     * thermal spoolers take jobs one at a time, and a single job with copies=N
     * would print the same photo N times, which is the opposite of what a
     * multi-shot session is for.
     */
    const printOne = async (index: number): Promise<boolean> => {
      try {
        const composedBlob = await previews[index]!.exportImage()
        const useThermal = await shouldUseThermalPrint()

        if (useThermal) {
          const thermalBlob = await exportThermalImageBlob(composedBlob)
          const base64data = await blobToBase64DataUrl(thermalBlob)
          const jobId = `bahelastruk-${Date.now()}-${index + 1}`
          const printResult = await printThermalEscpos({
            jobId,
            imageData: base64data,
            copies: 1
          })
          if (!printResult.success) {
            cogoToast.error('Gagal mencetak karcis')
          }
          return printResult.success
        }

        const { printerName, settings } = getPrinterForFrame(
          userPreferences.frameId || (frames[0]?._id ?? '')
        )
        // Resolves only when the job was actually accepted; rejects otherwise.
        //
        // The explicit duplicate key is load-bearing. Every ticket in this
        // session is a separate job fired seconds apart, which is exactly the
        // shape the 5-second duplicate guard is built to suppress; naming each
        // ticket says "these are N intended prints, not one print repeated"
        // rather than leaving it to whether the photos happen to differ.
        await initiatePrint(
          composedBlob,
          printerName,
          1,
          settings,
          true,
          `${cycleKey}-ticket-${index + 1}`,
          'session',
          // Without this the print event lands with no project and no session,
          // and a karcis that never came out cannot be traced back to the
          // booth that lost it — every karcis print ever logged was
          // unattributable, which is why a per-project query returned nothing.
          { projectId: projectId || undefined, sessionId: sessionIdRef.current || undefined }
        )
        return true
      } catch (error) {
        console.error('Print failed:', error)
        logKarcis(
          'Karcis print failed',
          {
            ticket: index + 1,
            of: photos.length,
            error: error instanceof Error ? error.message : String(error)
          },
          'print'
        )
        cogoToast.error('Gagal mencetak foto')
        return false
      }
    }

    // Sequential, and it has to be: the printers behind this flow are 80mm
    // thermal units that cut between jobs, so overlapping jobs interleave two
    // karcis onto one strip.
    const printJob = (async (): Promise<number> => {
      let succeeded = 0
      for (let i = 0; i < photos.length; i++) {
        if (await printOne(i)) succeeded += 1
      }
      return succeeded
    })()

    // Animate the bar while the job runs instead of after it — composing + sending can take
    // tens of seconds, which used to leave the operator staring at an empty grey bar.
    startPrintingProgress(printJob)
    const succeeded = await printJob
    const printed = succeeded > 0

    if (succeeded < photos.length) {
      logKarcis(
        'Karcis session printed short',
        { requested: photos.length, printed: succeeded },
        'print'
      )
    }

    // The karcis is out (or definitively isn't): close the session here rather
    // than at the reset, so the outcome and the print logs reach the server
    // while the result screen is still counting down.
    endKarcisCycle(
      printed,
      printed
        ? `print_succeeded_${succeeded}_of_${photos.length}`
        : `print_failed_0_of_${photos.length}`
    )
  }

  /**
   * Drives the "diproses" bar. When a print job is supplied the bar creeps to
   * PROGRESS_HOLD_AT and waits there until the job settles, so "silahkan disobek"
   * still only appears once the karcis has actually been sent to the printer.
   */
  const startPrintingProgress = (printJob?: Promise<unknown>): void => {
    const PROGRESS_HOLD_AT = 90
    let progress = 0
    let printSettled = !printJob
    void printJob?.then(
      () => {
        printSettled = true
      },
      () => {
        printSettled = true
      }
    )

    clearPrintProgress()
    printProgressIntervalRef.current = setInterval(() => {
      const ceiling = printSettled ? 100 : PROGRESS_HOLD_AT
      if (progress < ceiling) {
        progress += 2
        setPrintProgress(progress)
      }

      if (progress >= 100) {
        clearPrintProgress()
        setTimeout(() => {
          setCurrentStage(5)
          startResultDisplay()
        }, 1000)
      }
    }, 100)
  }

  const clearPrintProgress = (): void => {
    if (printProgressIntervalRef.current) {
      clearInterval(printProgressIntervalRef.current)
      printProgressIntervalRef.current = null
    }
  }

  const startResultDisplay = (): void => {
    // Start blinking effect
    const blinkInterval = setInterval(() => {
      setIsBlinking((prev) => !prev)
    }, 800)

    resultDisplayCountdown.setOnComplete(() => {
      clearInterval(blinkInterval)
      latest.current.resetToStage1()
    })
    resultDisplayCountdown.start()
  }

  const cleanupPayment = async (explicitOrderId?: string): Promise<void> => {
    // Timers and the unmount handler hold frozen copies of this function, so the
    // order to cancel has to come from `latest` — the captured `transaction` is
    // whatever it was when that copy was made, usually the empty initial one.
    // Callers that already know which order they are retiring pass it explicitly,
    // so a concurrent reload can't redirect the cancel onto the new order.
    const { orderId: currentOrderId, status } = latest.current.transaction
    const orderId = explicitOrderId || currentOrderId
    // Never cancel an order we know was paid; anything else is fair game.
    const isPaid = orderId === currentOrderId && status === 'settlement'
    if (orderId && !isPaid) {
      try {
        await fetch(`${process.env.VITE_API_URL}/api/payments/${orderId}`, {
          method: 'DELETE',
          credentials: 'include'
        })
        console.log('Payment cancelled:', orderId)
      } catch (error) {
        console.error('Error cancelling payment:', error)
      }
    }
  }

  /** Release every blob URL this cycle minted — one per karcis, not one per session. */
  const revokeCapturedImages = (): void => {
    for (const url of capturedPhotoUrlsRef.current) URL.revokeObjectURL(url)
    capturedPhotoUrlsRef.current = []
  }

  /**
   * Open the session for the customer about to use the booth.
   *
   * Until this existed the store's session id stayed at '' for the life of the
   * process, which silently disabled BOTH uploads: `uploadSessionState` returns
   * early without an id, and `logActivity` drops entries that have no session to
   * hang off — so the karcis booths had no sessions and no activity logs at all,
   * including the remote-print outcomes logged from printUtils.
   */
  const beginKarcisCycle = (): void => {
    sessionIdRef.current = startKarcisSession(projectId, KARCIS_PRICE, ticketCount)
  }

  /**
   * Close the current cycle, if one is open. Safe to call twice — the ref is
   * cleared first, so the print path and the reset path racing each other end
   * the session exactly once.
   */
  const endKarcisCycle = (printed: boolean, reason: string): void => {
    const sessionId = sessionIdRef.current
    if (!sessionId) return
    sessionIdRef.current = null
    void finishKarcisSession(sessionId, { printed, reason })
  }

  const resetToStage1 = (): void => {
    // Close the outgoing cycle if the print path didn't already (the supervisor
    // writes off stuck stages through here, and those sessions must still be
    // recorded — a session that ends with isCompleted false and no print IS the
    // evidence that this booth is failing).
    endKarcisCycle(false, 'booth_reset')

    // Cleanup current payment before resetting
    cleanupPayment()

    setCurrentStage(1)
    setCapturedImageURL(null)
    setCapturedPhotos([])
    setResultPhotoIndex(0)
    shotsTakenRef.current = 0
    printPreviewRefs.current = []
    setQrUrl(null)
    setIsCheckingPayment(false)
    setPrintProgress(0)
    setIsBlinking(false)
    clearPrintProgress()
    clearQrRetry()
    qrExpiresAtRef.current = null
    qrOrderIdRef.current = null
    if (expiredReloadTimeoutRef.current) {
      clearTimeout(expiredReloadTimeoutRef.current)
      expiredReloadTimeoutRef.current = null
    }

    // Clear the composed photo/frame too. Leaving them set used to mask the stale-closure
    // bug above: session 2+ passed the guard on last session's leftovers.
    setSelectedFrame(undefined)
    setSelectedPhotos([])
    revokeCapturedImages()

    // Reset transaction state — including the voucher. A code that survived
    // into the next cycle would give the next guest a free session on somebody
    // else's quota and charge the used code twice.
    setVoucher(null)
    setVoucherOpen(false)
    setVoucherError('')
    setVoucherApplying(false)
    setTransaction({
      orderId: null,
      amount: KARCIS_PRICE,
      status: 'pending'
    })

    paymentCountdown.stop()
    captureCountdownHook.stop()
    resultDisplayCountdown.stop()

    // Open the next customer's session before the countdown can start their
    // payment — the gateway quote is tagged with the session id.
    beginKarcisCycle()

    // Auto-start the next session — but only where nobody can start it by hand.
    // On a touch booth the welcome screen is the guest's cue to begin, and a
    // timer that walks past it quotes a QR to an empty booth.
    if (variant.autoStartWelcome) {
      waitCountdown.setOnComplete(() => {
        setCurrentStage(2)
        void latest.current.initiatePayment()
      })
      waitCountdown.start()
    }
  }

  /** Guest tapped "Mulai Sesi" (touch booths only — see karcisVariant.ts). */
  const handleStartTapped = (): void => {
    if (latest.current.currentStage !== 1) return
    setCurrentStage(2)
    void initiatePayment()
  }

  const applyFilter = (ctx: CanvasRenderingContext2D): void => {
    if (selectedFilter?.filterMode === 'lut') {
      applyLUTThenOverlay(ctx, selectedFilter)
    } else if (selectedFilter?.adjustments) {
      filterUtils.applyAdjustments(ctx, selectedFilter.adjustments)
      applyFilterOverlayImage(ctx, selectedFilter?.overlay)
    }
  }

  /**
   * `useCountdown` freezes whatever callback it was last handed (it stores it in a
   * ref and never refreshes it), so every timer on this page runs the closure from
   * the render that armed it — reading state as it was seconds or minutes ago.
   * That is what silently stranded the karcis print (#199) and then the QR here.
   * Long-lived callbacks read state and re-enter the flow through this bag instead.
   */
  const latest = useRef({
    currentStage,
    transaction,
    ticketCount,
    voucher,
    payableAmount,
    initiatePayment,
    handlePaymentExpired,
    handleCapture,
    resetToStage1
  })
  useEffect(() => {
    latest.current = {
      currentStage,
      transaction,
      ticketCount,
      voucher,
      payableAmount,
      initiatePayment,
      handlePaymentExpired,
      handleCapture,
      resetToStage1
    }
  })

  useEffect(() => {
    // Re-arm on mount. `isMounted` gates every recovery path on this page, and the
    // cleanup below only ever set it to false — one stray unmount/remount pair and
    // the whole self-healing net was dead for the rest of the page's life.
    isMounted.current = true
    stageEnteredAtRef.current = Date.now()

    beginKarcisCycle()

    if (variant.autoStartWelcome) {
      autoStartCountdown.setOnComplete(() => {
        setCurrentStage(2)
        void latest.current.initiatePayment()
      })
      autoStartCountdown.start()
    }

    return (): void => {
      isMounted.current = false
      // Close the open cycle so its state and buffered activity logs are flushed
      // rather than dying with the component (operator exit, route change).
      endKarcisCycle(false, 'page_unmounted')
      // Cleanup payment on unmount
      cleanupPayment()
      clearPrintProgress()
      clearQrRetry()
      if (expiredReloadTimeoutRef.current) clearTimeout(expiredReloadTimeoutRef.current)
      revokeCapturedImages()
      paymentCountdown.stop()
      captureCountdownHook.stop()
      autoStartCountdown.stop()
      resultDisplayCountdown.stop()
      waitCountdown.stop()
    }
  }, [])

  // Note when the page enters a stage, so the supervisor can tell "busy" from "stuck".
  useEffect(() => {
    stageEnteredAtRef.current = Date.now()
  }, [currentStage])

  // A multi-shot session prints several karcis, so the result screen cycles
  // through them — otherwise the guest only ever sees the last one and has no
  // way to tell which photo landed on which ticket.
  useEffect(() => {
    if (currentStage !== 5 || capturedPhotos.length < 2) return undefined
    const cycle = setInterval(() => {
      setResultPhotoIndex((i) => (i + 1) % capturedPhotos.length)
    }, 1500)
    return (): void => clearInterval(cycle)
  }, [currentStage, capturedPhotos.length])

  /**
   * The supervisor: the only timer on this page that is not gated behind page
   * state, and the one that is expected to still be there when everything else
   * has stopped.
   *
   * Every previous recovery path — the 3s expiry reload, the 5s retry, the old
   * 15s watchdog — was armed from inside the flow it was meant to rescue and
   * re-armed by an effect keyed on the same state it was guarding. So they did
   * not fail independently: a renderer that stops running timers, or a stage that
   * moves while a reload is pending, silences all of them at once and leaves
   * nothing behind that could ever start them again. That is a booth stuck on
   * "Loading QR Code…" until a human touches it.
   *
   * This one mounts once, runs to unmount, and re-derives what the page *should*
   * be doing from the wall clock each pass. It repairs rather than counts, so
   * however long the renderer was throttled or suspended, the first pass after it
   * wakes up puts the booth back on a live QR.
   */
  useEffect(() => {
    let noQrSince: number | null = null

    const supervise = (): void => {
      if (!isMounted.current) return
      const now = Date.now()
      const stage = latest.current.currentStage

      if (stage === 2) {
        // A QR is on screen whose deadline has passed — the display countdown was
        // supposed to catch this, but it counts ticks, so a throttled renderer
        // leaves it reading minutes-remaining on a QR that died long ago.
        if (qrHasLapsed()) {
          console.warn('[BahelaReceipt] QR outlived its expiry — retiring it')
          noQrSince = now
          latest.current.handlePaymentExpired()
          return
        }

        if (qrExpiresAtRef.current !== null) {
          noQrSince = null
          return
        }

        // No live QR. Give the normal reload path its window, then take over.
        if (noQrSince === null) {
          noQrSince = now
          return
        }
        if (now - noQrSince >= QR_WATCHDOG_MS) {
          console.warn('[BahelaReceipt] payment stage still without a QR — re-initiating')
          noQrSince = now
          void latest.current.initiatePayment()
        }
        return
      }

      noQrSince = null

      // On a touch booth the welcome screen IS a destination — it waits for a
      // guest, which may be all afternoon. Sweeping it forward would quote a QR
      // to nobody and then expire it, forever.
      if (stage === 1 && !variant.autoStartWelcome) return

      const ceiling = karcisStageCeilingMs(stage, latest.current.ticketCount)
      if (!ceiling || now - stageEnteredAtRef.current <= ceiling) return
      stageEnteredAtRef.current = now

      // The welcome screen is a 5s waypoint, never a destination. If we are still
      // here the auto-start countdown didn't fire, so step forward directly rather
      // than through resetToStage1 — that would just re-arm the countdown that
      // already failed us.
      if (stage === 1) {
        console.warn('[BahelaReceipt] welcome screen never auto-started — advancing to payment')
        setCurrentStage(2)
        void latest.current.initiatePayment()
        return
      }

      // Stages that wait on hardware have no timeout of their own: a camera that
      // never returns a frame or a print that never settles parks the booth on a
      // screen with no way forward. Past the ceiling, write the session off.
      console.warn(`[BahelaReceipt] stage ${stage} exceeded ${ceiling}ms — resetting booth`)
      latest.current.resetToStage1()
    }

    const supervisor = setInterval(supervise, SUPERVISOR_MS)
    return (): void => clearInterval(supervisor)
  }, [])

  // Cleanup payment when leaving stage 2
  useEffect(() => {
    if (currentStage !== 2 && transaction.orderId && transaction.status !== 'settlement') {
      cleanupPayment()
      setQrUrl(null)
      setIsCheckingPayment(false)
    }
  }, [currentStage])

  useInterval(
    async () => {
      await checkPaymentStatus()
    },
    isCheckingPayment ? 5000 : null
  )

  /**
   * The guest typed a code.
   *
   * Reuses `/api/promos/apply`, the same endpoint the Bahela checkout has
   * always called, so a code behaves identically wherever it is entered —
   * expiry, quota, project scope and the cross-currency guard are all decided
   * server-side and none of them are re-implemented here.
   *
   * Two outcomes. A code that covers the whole karcis settles the session on
   * the spot and never touches the gateway: that is the Bilik Kotak reward and
   * every 100% code. A code that only takes something off cancels the QR on
   * screen and asks for a fresh quote at the lower figure — leaving the old QR
   * up would charge the guest the price they were shown before the discount.
   */
  const handleVoucherSubmit = async (rawCode: string): Promise<void> => {
    const code = rawCode.trim().toUpperCase()
    if (!code) {
      setVoucherError('Masukkan kode voucher')
      return
    }

    setVoucherApplying(true)
    setVoucherError('')
    try {
      const response = await fetch(`${process.env.VITE_API_URL}/api/promos/apply`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, projectId, amount: KARCIS_PRICE })
      })
      const data = await response.json().catch(() => null)

      if (!response.ok || !data?.isValid) {
        // The server's sentence, not ours: it is the only one that knows
        // WHICH rule refused the code, and "kode tidak berlaku" sends a guest
        // who is merely a day late to the team for an explanation.
        setVoucherError(data?.message || 'Kode voucher tidak dapat dipakai')
        return
      }

      const applied = readKarcisVoucher(KARCIS_PRICE, code, data)
      if (!applied) {
        setVoucherError('Kode voucher tidak dapat dipakai')
        return
      }

      setVoucher(applied)
      setVoucherOpen(false)
      logKarcis(
        'Karcis voucher applied',
        {
          code: applied.code,
          voucherId: applied.voucherId,
          voucherType: applied.voucherType,
          discount: applied.discount,
          payable: applied.payable
        },
        'payment'
      )

      if (applied.coversSession) {
        await settleWithVoucher(applied)
        return
      }

      // Partial discount: retire the quote the guest was shown and ask for one
      // at the new price. Pass the order id explicitly — a watchdog reload can
      // land between these two lines and redirect an unpinned cancel onto the
      // replacement.
      await cleanupPayment(latest.current.transaction.orderId || undefined)
      setQrUrl(null)
      setIsCheckingPayment(false)
      qrExpiresAtRef.current = null
      qrOrderIdRef.current = null
      qrRequestStartedAtRef.current = null
      setTransaction({ orderId: null, amount: applied.payable, status: 'pending' })
      cogoToast.success(`Voucher dipakai! Bayar Rp ${applied.payable.toLocaleString('id-ID')}`)
      void initiatePayment()
    } catch (error) {
      console.error('[BahelaReceipt] voucher apply failed:', error)
      setVoucherError('Gagal menghubungi server. Coba lagi.')
    } finally {
      setVoucherApplying(false)
    }
  }

  /**
   * A voucher covered the whole karcis: settle and shoot.
   *
   * Deliberately the same five steps as `performBypassPayment`, which is the
   * other way this booth reaches stage 3 without a gateway — cancel the live
   * quote, stop polling, mark settled, push the session, advance. The one
   * difference is that this upload carries the voucher, and it has to: that is
   * what tells the server to spend the code's single use. A free session
   * recorded without it leaves the code good forever.
   */
  const settleWithVoucher = async (applied: KarcisVoucherApplication): Promise<void> => {
    await cleanupPayment(latest.current.transaction.orderId || undefined)

    setIsCheckingPayment(false)
    setQrUrl(null)
    qrExpiresAtRef.current = null
    qrOrderIdRef.current = null
    qrRequestStartedAtRef.current = null
    clearQrRetry()
    paymentCountdown.stop()
    setRemainingTime(null)
    setTransaction({ orderId: null, amount: applied.amount, status: 'settlement' })

    recordKarcisPayment(
      sessionIdRef.current || '',
      {
        orderId: null,
        amount: applied.amount,
        status: 'settlement',
        engagedAt: new Date(),
        ...voucherFields(applied)
      },
      { upload: true }
    )

    cogoToast.success('Voucher berhasil! Siap-siap foto!')
    if (latest.current.currentStage === 2) {
      setCurrentStage(3)
      startCaptureCountdown()
    }
  }

  /**
   * Operator bypass. On the analog-TV karcis nobody could reach this button
   * (no touchscreen), so it shipped unguarded; on a touch deployment any guest
   * can find the top-left corner. PIN-gated with the same booth PIN as the
   * standard skip-payment chrome. No PIN configured = bypass stays locked
   * (Device Setup forces a PIN before the booth can be continued anyway).
   */
  const performBypassPayment = async (): Promise<void> => {
    // Cleanup payment when bypassing
    await cleanupPayment()

    setIsCheckingPayment(false)
    setQrUrl(null) // Clear QR URL
    setTransaction((t) => ({ ...t, status: 'settlement' }))

    // A bypassed session still has to exist server-side, or the print logs that
    // follow have nothing to attach to. Amount 0: nobody paid.
    recordKarcisPayment(
      sessionIdRef.current || '',
      { orderId: null, amount: 0, status: 'settlement', engagedAt: new Date() },
      { upload: true }
    )

    // Only advance to stage 3 if we're currently on stage 2 (payment stage)
    if (currentStage === 2) {
      setCurrentStage(3)
      startCaptureCountdown()
    }
  }

  /** Open operator chrome, PIN-gated or not depending on who can reach it. */
  const requestOperatorAction = (action: 'bypass' | 'printer'): void => {
    if (variant.guardOperatorChrome) {
      setPinPrompt(action)
      return
    }
    runOperatorAction(action)
  }

  const runOperatorAction = (action: 'bypass' | 'printer'): void => {
    if (action === 'printer') {
      setThermalSettingsOpen(true)
      return
    }
    logKarcis('Payment skipped by operator', { customPath: 'bahelastruk' }, 'payment')
    void performBypassPayment()
  }

  const handlePinSubmit = (pin: string): void => {
    if (pin !== password) {
      cogoToast.error('PIN salah. Coba lagi!')
      return
    }
    const action = pinPrompt
    setPinPrompt(null)
    if (action) runOperatorAction(action)
  }

  // Call all hooks at the top level
  const cachedBackground = useCachedBackgroundImage(background)

  // Canvas aspect for the active orientation — the one runtime input the
  // fit-to-stage sizing needs (same source View's ScaleToFit shell uses).
  const { isPortrait: deviceIsPortrait } = useDeviceOrientation()
  const designCanvas = getDesignCanvasSizeForOrientation(
    config,
    deviceIsPortrait ? 'portrait' : 'landscape'
  )
  const hOverW = designCanvas.height / designCanvas.width

  /**
   * LAYOUT NOTE — canvas-native units only.
   *
   * This page renders inside View's ScaleToFit shell: a fixed design canvas
   * (the project's screenSize, 1920×1080 for the karcis projects) uniformly
   * scaled to whatever monitor/tablet runs the booth. Viewport units (vw/vh/
   * h-screen) measure the REAL screen and bypass that transform — mixing them
   * with canvas-relative sizes is exactly what broke the layout on the iPad.
   *
   * Rules for every element below:
   *   - stage panels are 20% of the 500% strip (= one canvas screen each),
   *   - widths are % of the canvas (min() px caps match the 1920px reference),
   *   - overlays inside frame art are % insets of the frame, so art and
   *     content stay aligned at any canvas size and in portrait rotation,
   *   - px values are canvas pixels (design-space), never screen pixels.
   */
  return (
    <div className="relative w-full h-full overflow-hidden">
      {/* Hidden Bypass Payment Button (top left) — PIN-gated */}
      <button
        className="absolute top-4 left-4 w-12 h-12 opacity-20 z-50 hover:opacity-40 transition-opacity"
        title="Bypass Payment"
        tabIndex={0}
        onClick={() => setPinPrompt('bypass')}
      >
        <FaMoneyBillWave className="text-white text-[28px]" />
      </button>

      {/* Voucher entry. The booth's own on-screen keyboard, because this iPad
          runs under Guided Access and the system keyboard cannot be summoned;
          `variant: 'voucher'` is the same uppercase-only layout (with the QR
          tab) the Studio checkout has always used. */}
      {voucherOpen && (
        <OnScreenKeyboard
          variant="voucher"
          modalTitle="Masukkan Kode Voucher"
          placeholder="Ketik kode vouchermu"
          submitLabel="Pakai"
          submitLoadingLabel="Mengecek..."
          error={voucherError}
          isLoading={voucherApplying}
          onInputChange={() => setVoucherError('')}
          onEnter={(value) => {
            void handleVoucherSubmit(value)
          }}
          onClose={() => {
            setVoucherOpen(false)
            setVoucherError('')
          }}
          // `!` because the component carries its own z-[10]: two Tailwind
          // utilities of equal specificity are resolved by CSS order, not by
          // class order, and without it the page's own z-50 operator chrome
          // (the payment bypass) sits on top of the keyboard where a guest can
          // reach it. Same reason the email modals pass !z-[100].
          overlayClassName="!z-[200]"
        />
      )}

      <PinModal
        isOpen={pinPrompt !== null}
        onClose={() => setPinPrompt(null)}
        onSubmit={handlePinSubmit}
        title={pinPrompt === 'printer' ? 'Pengaturan Printer' : 'Skip Payment'}
        subtitle={
          pinPrompt === 'printer'
            ? 'Masukkan PIN 6 digit untuk membuka pengaturan printer'
            : 'Masukkan PIN 6 digit untuk melewati pembayaran'
        }
      />

      {/* Hidden PrintPreview per karcis — each pinned to one photo for its whole
          life, so a composition can never pick up the previous shot. */}
      <div style={{ display: 'none' }}>
        {selectedFrame &&
          selectedPhotos.map((photo, i) => (
            <PrintPreview
              key={photo}
              ref={(el): void => {
                printPreviewRefs.current[i] = el
              }}
              frameConfig={{
                id: selectedFrame._id,
                baseImage: selectedFrame.thumbnail,
                drawParams: selectedFrame.drawParams
              }}
              selectedPhotos={[photo]}
              sessionId={id || ''}
              isInteractive={false}
            />
          ))}
      </div>

      {/* Flash effect */}
      <div id="flash" className="w-full h-full flash"></div>

      {/* Bahela Logo - Bottom Right (floating over the canvas; `fixed` would anchor
          to the transformed ancestor anyway, so say `absolute` and mean it) */}
      <img
        src={bahela}
        alt="Bahela Logo"
        className="absolute bottom-8 right-8 w-20 h-14 drop-shadow-2xl z-40"
      />

      {capabilities.thermalPrint && (
        <button
          type="button"
          className="absolute left-4 top-4 z-50 rounded-full bg-white/90 px-3 py-2 text-xs font-medium text-gray-800 shadow-lg"
          onClick={() => requestOperatorAction('printer')}
        >
          Printer
        </button>
      )}

      <BahelaThermalSettings
        open={thermalSettingsOpen}
        onClose={() => setThermalSettingsOpen(false)}
      />

      {/* Main Container with Fixed Background — 5 stacked canvas screens (the
          background strip art is 1024×3840 = 5 × one screen), so 500% of the
          page height, never 500vh: vh measures the physical viewport and drifts
          from the design canvas on tablets. */}
      <div
        className="w-full h-[500%] relative transition-transform duration-1000 ease-in-out"
        style={{
          transform: `translateY(-${stageTop(currentStage)})`,
          backgroundImage: `url(${cachedBackground})`,
          backgroundSize: '100% 100%',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center top'
        }}
      >
        {/* Stage 1: Welcome Screen */}
        <div
          className={`w-full h-[20%] flex flex-col items-center justify-center absolute transition-opacity duration-1000 ${
            currentStage === 1 ? 'opacity-100 visible' : 'opacity-0 invisible'
          }`}
          style={{ top: stageTop(1) }}
        >
          <img
            src={karcisKenanganBig}
            alt="Karcis Kenangan"
            className="h-auto mb-[3.5%] drop-shadow-2xl"
            style={{ width: frameWidthCss('karcisBig', hOverW), aspectRatio: artAspectCss('karcisBig') }}
          />
          <img
            src={mulaiSesi}
            alt="Mulai Sesi"
            className="h-auto drop-shadow-xl cursor-pointer hover:scale-105 transition-transform"
            style={{ width: frameWidthCss('mulaiSesi', hOverW), aspectRatio: artAspectCss('mulaiSesi') }}
            onClick={handleStartTapped}
          />
        </div>

        {/* Stage 2: Preview & Payment */}
        <div
          className={`w-full h-[20%] flex flex-col items-center pt-[4.5%] absolute transition-opacity duration-1000 ${
            currentStage === 2 ? 'opacity-100 visible' : 'opacity-0 invisible'
          }`}
          style={{ top: stageTop(2) }}
        >
          {/* Title */}
          <img
            src={karcisKenanganSmall}
            alt="Karcis Kenangan"
            className="h-auto mb-[2%] drop-shadow-xl"
            style={{ width: frameWidthCss('titleSmall', hOverW), aspectRatio: artAspectCss('titleSmall') }}
          />

          {/* Content row: camera preview window + payment window, both fitted to the stage */}
          <div className="flex w-full min-h-0 items-start justify-center gap-[3%]">
            <ArtFrame art={pratinjau} artKey="pratinjau" alt="Pratinjau" hOverW={hOverW}>
              <div className="flex h-full w-full items-center justify-center p-[2%]">
                {/* Square media box: Preview fills whatever box it gets, so the box
                    itself must carry the 1:1 shape and the clipping. */}
                <div className="relative h-full aspect-square max-w-full overflow-hidden rounded-md bg-gray-900">
                  {isCameraConnected ? (
                    <Preview
                      key={`preview-${selectedCamera}-${cameraSettings.captureRatio}-${cameraSettings.mirror}-${cameraSettings.previewRotation}-${cameraSettings.previewQuality?.maxWidth}-${cameraSettings.previewQuality?.maxHeight}-${cameraSettings.previewQuality?.quality}-${cameraSettings.previewQuality?.resizeQuality}`}
                      adjustments={selectedFilter?.adjustments}
                      filter={selectedFilter?._id}
                      postLUTAdjustments={selectedFilter?.postLUTAdjustments}
                      ratio={1 / 1}
                      cameraKey={userPreferences.selectedCamera as 'primary' | 'secondary'}
                    />
                  ) : (
                    <div className="flex h-full w-full flex-col items-center justify-center bg-gray-800 text-white">
                      <div className="text-center">
                        <div className="text-6xl mb-4">📷</div>
                        <p className="text-xl font-semibold mb-2">Kamera Belum Terhubung</p>
                        <p className="text-sm opacity-75">Silakan hubungkan kamera terlebih dahulu</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </ArtFrame>

            <ArtFrame art={bayar} artKey="bayar" alt="Bayar" hOverW={hOverW}>
              {qrUrl ? (
                <div className="flex h-full w-full flex-col items-center justify-center">
                  <img
                    src={qrUrl}
                    alt="QR Code Pembayaran"
                    className="h-[56%] aspect-square rounded-xl shadow-xl"
                    crossOrigin="anonymous"
                  />
                  <p className="mt-[2%] text-[32px] font-mono font-bold text-gray-800">
                    {remainingTime || '...'}
                  </p>
                  {voucher && (
                    <p className="text-[20px] font-mono text-green-700">
                      Voucher {voucher.code} −Rp{' '}
                      {voucher.discount.toLocaleString('id-ID')}
                    </p>
                  )}
                  {canEnterVoucher && !voucher && (
                    <button
                      type="button"
                      onClick={() => {
                        setVoucherError('')
                        setVoucherOpen(true)
                      }}
                      className="mt-[2%] rounded-full border-2 border-gray-800 px-[6%] py-[1.5%] text-[22px] font-mono font-bold text-gray-800 active:bg-gray-800 active:text-white"
                    >
                      Punya voucher?
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex h-full w-full flex-col items-center justify-center gap-4">
                  <Spinner />
                  <p className="text-[18px] font-mono text-gray-700">Loading QR Code...</p>
                </div>
              )}
            </ArtFrame>
          </div>
        </div>

        {/* Stage 3: Capture Session */}
        <div
          className={`w-full h-[20%] flex items-center justify-center absolute transition-opacity duration-1000 ${
            currentStage === 3 ? 'opacity-100 visible' : 'opacity-0 invisible'
          }`}
          style={{ top: stageTop(3) }}
        >
          <ArtFrame
            art={pemutarFoto}
            artKey="pemutarFoto"
            alt="Pemutar Foto"
            hOverW={hOverW}
            shadowClass="drop-shadow-2xl"
          >
            <div className="flex h-full w-full items-center justify-center p-[1.5%]">
              <div className="relative h-full aspect-square max-w-full overflow-hidden rounded-lg bg-black">
                {capturedImageURL ? (
                  /* Show captured image immediately after taking photo */
                  <img
                    src={capturedImageURL}
                    alt="Foto Hasil"
                    className="w-full h-full object-cover"
                    crossOrigin="anonymous"
                  />
                ) : isCameraConnected ? (
                  /* Show camera preview during countdown */
                  <Preview
                    adjustments={selectedFilter?.adjustments}
                    filter={selectedFilter?._id}
                    postLUTAdjustments={selectedFilter?.postLUTAdjustments}
                    ratio={1 / 1}
                    cameraKey={userPreferences.selectedCamera as 'primary' | 'secondary'}
                  />
                ) : (
                  /* Show camera not connected message */
                  <div className="w-full h-full flex flex-col items-center justify-center text-white">
                    <div className="text-center">
                      <div className="text-8xl mb-4">📷</div>
                      <p className="text-2xl font-semibold mb-2">Kamera Belum Terhubung</p>
                      <p className="text-lg opacity-75">
                        Silakan hubungkan kamera untuk melanjutkan
                      </p>
                    </div>
                  </div>
                )}

                {/* Which karcis is being shot. A guest who paid for three needs to
                    know two more are coming, or the first flash reads as "done". */}
                {ticketCount > 1 && (
                  <div className="absolute left-[4%] top-[4%] rounded-full bg-black/60 px-[3%] py-[1.5%] text-[36px] font-mono font-bold leading-none text-white">
                    {Math.min(capturedPhotos.length + (capturedImageURL ? 0 : 1), ticketCount)}/
                    {ticketCount}
                  </div>
                )}

                {/* Countdown Overlay - centered over preview, only show during countdown */}
                {!capturedImageURL && (
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <div className="text-[200px] font-mono font-bold text-white leading-none animate-pulse drop-shadow-2xl bg-black bg-opacity-50 rounded-full w-[200px] h-[200px] flex items-center justify-center">
                      {captureCountdown}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </ArtFrame>
        </div>

        {/* Stage 4: Printing Process */}
        <div
          className={`w-full h-[20%] flex items-center justify-center absolute transition-opacity duration-1000 ${
            currentStage === 4 ? 'opacity-100 visible' : 'opacity-0 invisible'
          }`}
          style={{ top: stageTop(4) }}
        >
          {/* The art window here is the free band below the printed "Tunggu
              Sebentar" text — the progress bar's home at any frame size. */}
          <ArtFrame
            art={diproses}
            artKey="diproses"
            alt="Diproses"
            hOverW={hOverW}
            shadowClass="drop-shadow-2xl"
          >
            <div className="flex h-full w-full items-end">
              <div className="flex h-[85%] w-full items-center gap-[1%] rounded-lg bg-gray-400 px-[1.5%] overflow-hidden">
                {/* 20 fixed slots that share the track width — the fill ratio is
                    what reads as progress, so the count no longer has to guess
                    how many 30px bars a particular monitor can fit. */}
                {((): JSX.Element[] => {
                  const SLOTS = 20
                  const litSlots = Math.ceil((printProgress / 100) * SLOTS)
                  return Array.from({ length: SLOTS }, (_, i) => (
                    <div
                      key={i}
                      className={`h-[70%] min-w-0 flex-1 rounded-md border-2 border-black bg-[#287EA1] ${
                        i < litSlots ? '' : 'opacity-0'
                      }`}
                    />
                  ))
                })()}
              </div>
            </div>
          </ArtFrame>
        </div>

        {/* Stage 5: Photo Result */}
        <div
          className={`w-full h-[20%] flex flex-col items-center justify-center absolute transition-opacity duration-1000 ${
            currentStage === 5 ? 'opacity-100 visible' : 'opacity-0 invisible'
          }`}
          style={{ top: stageTop(5) }}
        >
          {/* The measured gray viewer area of this art is exactly square, so the
              1:1 capture fills it edge to edge. */}
          <ArtFrame
            art={pemutarMedia}
            artKey="pemutarMedia"
            alt="Pemutar Media"
            hOverW={hOverW}
            shadowClass="drop-shadow-2xl"
            className="mb-[1.5%]"
          >
            {capturedPhotos.length > 0 ? (
              <img
                key={resultPhotoIndex}
                src={capturedPhotos[resultPhotoIndex] ?? capturedPhotos[0]}
                alt="Foto Hasil"
                className="h-full w-full object-cover"
                crossOrigin="anonymous"
              />
            ) : null}
          </ArtFrame>

          <img
            src={silahkanDisobek}
            alt="Silahkan Disobek"
            className={`h-auto transition-opacity duration-700 ${
              isBlinking ? 'opacity-100' : 'opacity-50'
            }`}
            style={{ width: frameWidthCss('silahkan', hOverW), aspectRatio: artAspectCss('silahkan') }}
          />
        </div>
      </div>

      <style>{`
        .flash {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: white;
          opacity: 0;
          pointer-events: none;
          z-index: 1000;
        }
        
        .flash.active {
          animation: flash 0.5s ease-out;
        }
        
        @keyframes flash {
          0% { opacity: 0; }
          50% { opacity: 0.8; }
          100% { opacity: 0; }
        }
      `}</style>
    </div>
  )
}

export default BahelaReceiptPage
